import React from 'react';
import { 
  Paper, 
  Typography, 
  Stack,
  Divider,
  Button,
  CircularProgress,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import { LeadData } from '../types';

export interface WorkflowProgressProps {
  businessCount?: number;
  totalBusinessCount?: number;
  contactCount?: number;
  businessWithContactsCount?: number;
  exportReady?: boolean;
  isLoadingBusinesses?: boolean;
  isLoadingContacts?: boolean;
  onExport?: (results: LeadData[]) => void;
  results?: LeadData[];
}

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({ 
  businessCount = 0,
  totalBusinessCount = 0,
  contactCount = 0,
  businessWithContactsCount = 0,
  exportReady = false,
  isLoadingBusinesses = false,
  isLoadingContacts = false,
  onExport,
  results = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine if export is truly ready - all businesses and contacts are fetched
  const isExportTrulyReady = exportReady && 
    businessCount > 0 && 
    (totalBusinessCount === 0 || businessCount >= totalBusinessCount) &&
    contactCount > 0;

  // Format the business count display to handle cases where we exceed the target
  const formattedBusinessCount = () => {
    if (totalBusinessCount > 0 && businessCount > totalBusinessCount) {
      return `${businessCount} (target: ${totalBusinessCount})`;
    } else if (totalBusinessCount > 0) {
      return `${businessCount} of ${totalBusinessCount}`;
    }
    return businessCount.toString();
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Businesses Section */}
        <Box sx={{ 
          flex: 1, 
          textAlign: 'center', 
          p: isMobile ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <BusinessIcon color="primary" />
            <Typography variant="body1">
              <strong>Businesses</strong>
            </Typography>
            {isLoadingBusinesses && (
              <CircularProgress 
                size={16} 
                color="primary" 
                sx={{ 
                  ml: 1,
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }} 
              />
            )}
          </Stack>
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
            {formattedBusinessCount()}
          </Typography>
        </Box>
        
        {isMobile ? (
          <Divider orientation="horizontal" flexItem sx={{ my: 1 }} />
        ) : (
          <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
        )}
        
        {/* Contacts Section */}
        <Box sx={{ 
          flex: 1, 
          textAlign: 'center', 
          p: isMobile ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <PersonIcon color="primary" />
            <Typography variant="body1">
              <strong>Contacts</strong>
            </Typography>
            {isLoadingContacts && (
              <CircularProgress 
                size={16} 
                color="primary" 
                sx={{ 
                  ml: 1,
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }} 
              />
            )}
          </Stack>
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
            {contactCount} {businessWithContactsCount > 0 ? `in ${businessWithContactsCount} ${isMobile ? 'biz' : 'businesses'}` : ''}
          </Typography>
        </Box>
        
        {isMobile ? (
          <Divider orientation="horizontal" flexItem sx={{ my: 1 }} />
        ) : (
          <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
        )}
        
        {/* Verification Section */}
        <Box sx={{ 
          flex: 1, 
          textAlign: 'center', 
          p: isMobile ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          opacity: 0.7
        }}>
          <Tooltip title="Coming soon">
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <VerifiedUserIcon color="disabled" />
              <Typography variant="body1" color="text.secondary">
                <strong>Verification</strong>
              </Typography>
            </Stack>
          </Tooltip>
          <Typography variant="h6" sx={{ mt: 1 }} color="text.secondary">
            Locked <LockIcon fontSize="small" color="disabled" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
          </Typography>
        </Box>
        
        {isMobile ? (
          <Divider orientation="horizontal" flexItem sx={{ my: 1 }} />
        ) : (
          <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />
        )}
        
        {/* Export Section */}
        <Box sx={{ 
          flex: 1, 
          textAlign: 'center', 
          p: isMobile ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <FileDownloadIcon color={isExportTrulyReady ? "success" : "disabled"} />
            <Typography variant="body1">
              <strong>Export</strong>
            </Typography>
          </Stack>
          <Box sx={{ mt: 1 }}>
            {isExportTrulyReady && onExport && results && results.length > 0 ? (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={() => onExport(results)}
              >
                Export CSV
              </Button>
            ) : (
              <Typography variant="h6" color="text.secondary">
                {businessCount > 0 && contactCount > 0 ? "Preparing..." : "Pending"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default WorkflowProgress; 